import type { AxleListing } from '@contracts/axle/AxleListing';
import type { TractorWithAxlesResponse } from '@contracts/tractor/TractorWithAxlesResponse';
import { useAxleList } from '@src/axles/hooks/useAxleList';
import type { CollapsibleOrderSectionProps } from '@src/orders/CollapsibleOrderSection';
import { CollapsibleOrderSection } from '@src/orders/CollapsibleOrderSection';
import { AxleSummary } from '@src/orders/domain/AxleSummary';
import type { CheckedFields } from '@src/orders/domain/CheckedFields';
import { getSectionFields } from '@src/orders/domain/CheckedFields';
import { editOrderHaulingEquipmentEvents } from '@src/orders/EditOrderEvents';
import { useClient } from '@src/orders/hooks/useClient';
import type { AllAxlesInformation } from '@src/orders/PermitFormSections/axlesConfiguration/AxleInformationTypes';
import { fillAxleInformation } from '@src/orders/PermitFormSections/axlesConfiguration/FillAxleInformation';
import { AddTractorDrawer } from '@src/orders/PermitFormSections/haulingEquipment/AddTractorDrawer';
import { EditTractorDrawer } from '@src/orders/PermitFormSections/haulingEquipment/EditTractorDrawer';
import { TractorDimensionsSection } from '@src/orders/PermitFormSections/haulingEquipment/TractorDimensionsSection';
import { TractorInformationSection } from '@src/orders/PermitFormSections/haulingEquipment/TractorInformationSection';
import { TractorLicenseSection } from '@src/orders/PermitFormSections/haulingEquipment/TractorLicenseSection';
import { TractorOwnerSection } from '@src/orders/PermitFormSections/haulingEquipment/TractorOwnerSection';
import { TrailerInformationSection } from '@src/orders/PermitFormSections/haulingEquipment/TrailerInformationSection';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import {
  TractorActualWeightField,
  TractorGroundHeightField,
  TractorGVWField,
  TractorHeightField,
  TractorLengthField,
  TractorOuterBridgeLengthField,
  TractorUnladenWeightField,
  TractorWheelBaseField,
  TractorWidthField,
} from '@src/permitField/TractorDimensionsFields';
import {
  TractorAxleCountField,
  TractorBodyStyleField,
  TractorFuelTypeField,
  TractorIdField,
  TractorLicensePlateNumberField,
  TractorLicensePlateStateField,
  TractorMakeField,
  TractorModelNumberField,
  TractorTypeField,
  TractorUnitNumberField,
  TractorVINField,
  TractorYearField,
  VehicleOfSpecialDesignField,
} from '@src/permitField/TractorInformationFields';
import {
  TractorLicenseEffectiveDateField,
  TractorLicenseExpirationDateField,
  TractorLicenseIsApportionedPlateField,
  TractorLicensePlateTypeField,
  TractorLicensePlateYearField,
} from '@src/permitField/TractorLicenseFields';
import { TrailersFormName } from '@src/permitField/TrailerInformationFields';
import { useEffect, useRef, useState } from 'react';
import type { UseFieldArrayRemove, UseFormReturn } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

const emptyTractor = {
  id: '',
  axles: [],
  unitNumber: '',
  model: '',
  make: '',
  specialDesign: false,
  axleCount: '',
  year: null,
  vin: '',
  fuelType: '',
  type: '',
  bodyStyle: '',
  licensePlate: '',
  licensePlateState: '',
  licensePlateEffectiveDate: null,
  licensePlateExpirationDate: null,
  licensePlateType: '',
  licensePlateYear: null,
  licensePlateIsApportioned: false,
  length: null,
  width: null,
  height: null,
  wheelBase: null,
  groundHeight: null,
  unladenWeight: null,
  actualWeight: null,
  gvw: null,
  outerBridgeLength: null,
};

type Props = {
  clientId: string;
  //oxlint-disable-next-line
  methods: UseFormReturn<any>;
  readonly?: boolean;
  checkedFields: CheckedFields;
} & Omit<CollapsibleOrderSectionProps, 'children'>;

export const HaulingEquipmentSection = ({
  methods,
  checkedFields,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { axles: persistedAxles, isLoading: isLoadingAxles } = useAxleList(
    props.clientId,
  );
  const fields = [
    AxleFormName,
    TractorUnitNumberField.fieldName,
    TractorMakeField.fieldName,
    TractorModelNumberField.fieldName,
    TractorFuelTypeField.fieldName,
    TractorBodyStyleField.fieldName,
    TractorTypeField.fieldName,
    TractorYearField.fieldName,
    TractorVINField.fieldName,
    VehicleOfSpecialDesignField.fieldName,
    TractorLicensePlateNumberField.fieldName,
    TractorLicensePlateStateField.fieldName,
    TractorLicenseEffectiveDateField.fieldName,
    TractorLicenseExpirationDateField.fieldName,
    TractorLicensePlateTypeField.fieldName,
    TractorLicensePlateYearField.fieldName,
    TractorLicenseIsApportionedPlateField.fieldName,
    TractorLengthField.fieldName,
    TractorWidthField.fieldName,
    TractorHeightField.fieldName,
    TractorWheelBaseField.fieldName,
    TractorGroundHeightField.fieldName,
    TractorUnladenWeightField.fieldName,
    TractorActualWeightField.fieldName,
    TractorGVWField.fieldName,
    TractorOuterBridgeLengthField.fieldName,
  ];

  const { remove: removeTractorAxle } = useFieldArray({
    control: methods.control,
    name: TractorAxlesFormUtils.pathToAxles,
  });

  const [addTractorDrawerOpen, setAddTractorDrawerOpen] = useState(false);
  const [editTractorDrawerOpen, setEditTractorDrawerOpen] = useState(false);

  const axleFields = getSectionFields(
    'axlesConfiguration',
    'axles',
    checkedFields,
  );

  const [trailers, formTractorAxles, tractorId] = methods.watch([
    TrailersFormName,
    TractorAxlesFormUtils.pathToAxles,
    TractorIdField.fieldName,
  ]);
  const summary = AxleSummary.fromFormAndPersistedValues(
    trailers ?? [],
    {
      id: tractorId,
      axles: formTractorAxles,
    },
    persistedAxles,
  );
  const { client, isLoading: isLoadingClient } = useClient({
    id: props.clientId,
  });
  const previousTractor = useRef<string>(tractorId);
  const tractorAxles = summary.listTractorAxles();

  useEffect(() => {
    if (previousTractor.current != tractorId && !props.readonly) {
      previousTractor.current = tractorId;
      removeAxles(removeTractorAxle);
      updateAxlesValues(
        methods.setValue,
        tractorAxles,
        axleFields,
        TractorAxlesFormUtils.pathToAxles,
      );
    }
  }, [
    methods,
    tractorId,
    trailers,
    tractorAxles,
    axleFields,
    removeTractorAxle,
    props.readonly,
  ]);

  const selectTractor = (
    selectedTractor: TractorWithAxlesResponse | undefined,
  ) => {
    if (selectedTractor) {
      fillTractorSection(methods.setValue, selectedTractor, [
        ...fields,
        AxleFormName,
      ]);
      removeAxles(removeTractorAxle);
      updateAxlesValues(
        methods.setValue,
        selectedTractor.axles,
        axleFields,
        TractorAxlesFormUtils.pathToAxles,
      );
    } else {
      fillTractorSection(methods.setValue, emptyTractor, [
        ...fields,
        AxleFormName,
      ]);
      removeAxles(removeTractorAxle);
      updateAxlesValues(
        methods.setValue,
        [],
        axleFields,
        TractorAxlesFormUtils.pathToAxles,
      );
    }
  };

  return (
    <CollapsibleOrderSection
      title={t('orderSections.haulingEquipment')}
      withMetricToggle
      isLoading={isLoadingAxles}
      {...editOrderHaulingEquipmentEvents}
    >
      <TractorInformationSection
        onAddTractor={() => setAddTractorDrawerOpen(true)}
        onEditTractor={() => setEditTractorDrawerOpen(true)}
        clientId={props.clientId}
        onTractorSelect={selectTractor}
        {...editOrderHaulingEquipmentEvents}
        fields={getSectionFields(
          'haulingEquipment',
          'tractorInformation',
          checkedFields,
        )}
      />
      <TractorLicenseSection
        {...editOrderHaulingEquipmentEvents}
        fields={getSectionFields(
          'haulingEquipment',
          'tractorLicense',
          checkedFields,
        )}
      />
      <TractorDimensionsSection
        {...editOrderHaulingEquipmentEvents}
        fields={getSectionFields(
          'haulingEquipment',
          'tractorDimensions',
          checkedFields,
        )}
      />
      <TractorOwnerSection
        {...editOrderHaulingEquipmentEvents}
        fields={getSectionFields(
          'haulingEquipment',
          'tractorOwner',
          checkedFields,
        )}
      />
      <TrailerInformationSection
        clientId={props.clientId}
        {...editOrderHaulingEquipmentEvents}
        fields={[
          ...getSectionFields(
            'haulingEquipment',
            'globalTrailers',
            checkedFields,
          ),
          ...getSectionFields('haulingEquipment', 'trailers', checkedFields),
        ]}
        axleFields={axleFields}
      />
      {!isLoadingClient && client && (
        <>
          <AddTractorDrawer
            isOpen={addTractorDrawerOpen}
            onClose={() => setAddTractorDrawerOpen(false)}
            client={client}
            onAdd={(newTractor) => {
              methods.setValue(TractorIdField.fieldName, newTractor.id);
              selectTractor(newTractor);
              setAddTractorDrawerOpen(false);
            }}
          />
          <EditTractorDrawer
            tractorId={tractorId}
            isOpen={editTractorDrawerOpen}
            onClose={() => setEditTractorDrawerOpen(false)}
            client={client}
            onUpdate={(updatedTractor) => {
              selectTractor(updatedTractor);
              setEditTractorDrawerOpen(false);
            }}
          />
        </>
      )}
    </CollapsibleOrderSection>
  );
};

const calculatedFields = [TractorAxleCountField.fieldName];
const fieldMappings = {
  axles: AxleFormName,
  unitNumber: TractorUnitNumberField.fieldName,
  make: TractorMakeField.fieldName,
  model: TractorModelNumberField.fieldName,
  fuelType: TractorFuelTypeField.fieldName,
  bodyStyle: TractorBodyStyleField.fieldName,
  type: TractorTypeField.fieldName,
  year: TractorYearField.fieldName,
  vin: TractorVINField.fieldName,
  specialDesign: VehicleOfSpecialDesignField.fieldName,
  licensePlate: TractorLicensePlateNumberField.fieldName,
  licensePlateState: TractorLicensePlateStateField.fieldName,
  licensePlateEffectiveDate: TractorLicenseEffectiveDateField.fieldName,
  licensePlateExpirationDate: TractorLicenseExpirationDateField.fieldName,
  licensePlateType: TractorLicensePlateTypeField.fieldName,
  licensePlateYear: TractorLicensePlateYearField.fieldName,
  licensePlateIsApportioned: TractorLicenseIsApportionedPlateField.fieldName,
  length: TractorLengthField.fieldName,
  axleCount: TractorAxleCountField.fieldName,
  height: TractorHeightField.fieldName,
  width: TractorWidthField.fieldName,
  wheelBase: TractorWheelBaseField.fieldName,
  groundHeight: TractorGroundHeightField.fieldName,
  unladenWeight: TractorUnladenWeightField.fieldName,
  actualWeight: TractorActualWeightField.fieldName,
  gvw: TractorGVWField.fieldName,
  outerBridgeLength: TractorOuterBridgeLengthField.fieldName,
};
const fillTractorSection = (
  //oxlint-disable-next-line
  setValue: UseFormSetValue<any>,
  values: Omit<TractorWithAxlesResponse, 'year'> & {
    year: number | null;
  },
  fields: string[],
) => {
  Object.entries(fieldMappings)
    .filter(([_, fieldName]) => !calculatedFields.includes(fieldName))
    .filter(
      ([_, fieldName]) =>
        fields.includes(fieldName) || fields.includes('axles'),
    )
    .forEach(([key, fieldName]) => {
      if (key === AxleFormName) {
        const value = values[key]?.length ?? 1;
        setValue(TractorAxleCountField.fieldName, value.toString(), {
          shouldValidate: true,
        });
      } else {
        const value = (values[key as keyof typeof values] ?? '') as
          | string
          | number;
        setValue(fieldName, value, {
          shouldValidate: true,
        });
      }
    });
};

const removeAxles = (remove: UseFieldArrayRemove) => {
  remove();
};

const updateAxlesValues = (
  setValue: UseFormSetValue<AllAxlesInformation>,
  axles: AxleListing[],
  fields: string[],
  vehiclePrefix: string,
) => {
  axles.forEach((axle, i) => {
    fillAxleInformation(i, axle, setValue, vehiclePrefix, fields);
  });
};
